import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Search, ArrowRight, Cloud } from 'lucide-react';
import { Button } from '../components/ui/button';

const NotFound = () => {
  React.useEffect(() => {
    document.title = '404 — Page Not Found | CARTIEAI';
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900 flex flex-col" data-testid="not-found-page">
      {/* Subtle nav */}
      <header className="px-4 py-5 border-b border-white/5">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="not-found-logo">
            <img src="/icons/cartie-emblem.png" alt="" className="w-7 h-7" />
            <span className="font-bold">CARTIE<span className="text-emerald-700">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
          </Link>
          <Link to="/" className="text-sm text-slate-600 hover:text-white">Back to home</Link>
        </div>
      </header>

      {/* Hero */}
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-emerald-500/10 mb-8 ring-1 ring-emerald-500/20">
            <Cloud className="w-10 h-10 text-emerald-700" />
          </div>

          <p className="text-[11px] font-bold tracking-[0.25em] text-emerald-700 mb-4">ERROR 404</p>
          <h1 className="text-5xl sm:text-6xl font-extrabold tracking-tight mb-5">
            This page got <span className="text-emerald-700">right-sized away.</span>
          </h1>
          <p className="text-lg text-slate-600 mb-10 max-w-lg mx-auto">
            The URL you tried doesn't exist (or moved). Don't worry — your cloud bill is still bleeding. Let's go fix that instead.
          </p>

          {/* Primary CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
            <Link to="/" data-testid="not-found-home-btn">
              <Button size="lg" className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white">
                <Home className="w-4 h-4 mr-2" />
                Take me home
              </Button>
            </Link>
            <Link to="/snowflake-health-score" data-testid="not-found-health-score-btn">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-slate-700 text-slate-700 hover:bg-white/5">
                Run free Snowflake audit
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {/* Popular destinations */}
          <div className="rounded-2xl bg-white/[0.02] border border-white/5 p-6 text-left">
            <div className="flex items-center gap-2 mb-4">
              <Search className="w-4 h-4 text-slate-600" />
              <p className="text-xs uppercase tracking-widest text-slate-600 font-semibold">Popular pages</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {[
                { to: '/about', label: 'Our Story', sub: 'Why we built CARTIEAI' },
                { to: '/pricing', label: 'Pricing', sub: 'Simple, transparent tiers' },
                { to: '/blog', label: 'Blog', sub: 'FinOps tactics & deep dives' },
                { to: '/snowflake-health-score', label: 'Free Snowflake Audit', sub: '60s · No signup' },
                { to: '/integrations', label: 'Integrations', sub: '6 LIVE clouds + more' },
                { to: '/contact', label: 'Contact us', sub: 'hello@cartieai.com' },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="group block rounded-lg px-3 py-2.5 hover:bg-white/[0.04] transition-colors"
                  data-testid={`not-found-link-${l.to.slice(1) || 'home'}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-700 group-hover:text-white">{l.label}</p>
                      <p className="text-xs text-slate-600">{l.sub}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-emerald-700 group-hover:translate-x-0.5 transition-all" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/5 px-4 py-6 text-center text-xs text-slate-600">
        &copy; 2026 CARTIEAI · <Link to="/about" className="text-emerald-700">Our Story</Link>
      </footer>
    </div>
  );
};

export default NotFound;
